import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  useWorkPathTemplates,
  useDeleteWorkPathTemplate,
  type WorkPathTemplate,
} from '@/hooks/useWorkPaths';
import { useNotify } from '@/stores/notificationStore';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { describeApiError } from '@/lib/describeApiError';

interface Props {
  onEdit: (template: WorkPathTemplate) => void;
  includeInactive: boolean;
}

function taskCountOf(template: WorkPathTemplate): number {
  if (typeof template.taskCount === 'number') return template.taskCount;
  return template.tasks?.length ?? 0;
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function WorkPathTemplatesList({ onEdit, includeInactive }: Props) {
  const { t } = useTranslation('workpaths');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const { data: templates, isLoading } = useWorkPathTemplates({ includeInactive });
  const deleteTemplate = useDeleteWorkPathTemplate();

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuTemplate, setMenuTemplate] = useState<WorkPathTemplate | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<WorkPathTemplate | null>(null);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    template: WorkPathTemplate,
  ) => {
    setMenuAnchor(event.currentTarget);
    setMenuTemplate(template);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuTemplate(null);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteTemplate.mutateAsync(confirmDelete.id);
      notify.success(t('template.deleted'));
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('errors.templateDeleteFailed') }));
    }
    setConfirmDelete(null);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!templates || templates.length === 0) {
    return <Alert severity="info">{t('template.noTemplates')}</Alert>;
  }

  return (
    <>
      <Stack spacing={2}>
        {templates.map((template) => (
          <Card
            key={template.id}
            variant="outlined"
            sx={{ opacity: template.isActive ? 1 : 0.6 }}
          >
            <CardContent
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                py: 1.5,
                '&:last-child': { pb: 1.5 },
              }}
            >
              <Box flex={1}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {template.name}
                </Typography>
                {template.description && (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {template.description}
                  </Typography>
                )}
                <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={t('template.taskCount', { count: taskCountOf(template) })}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={
                      template.isActive
                        ? t('template.active')
                        : t('template.inactive')
                    }
                    color={template.isActive ? 'success' : 'default'}
                    variant="outlined"
                  />
                  {template.isDefault && (
                    <Chip
                      size="small"
                      label={t('template.isDefault')}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  <Chip
                    size="small"
                    label={t('template.updatedAt', {
                      date: formatTimestamp(template.updatedAt),
                    })}
                    variant="outlined"
                  />
                </Stack>
              </Box>
              <Tooltip title={tc('actions')}>
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, template)}
                  aria-label={tc('actions')}
                >
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            if (menuTemplate) onEdit(menuTemplate);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{tc('edit')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setConfirmDelete(menuTemplate);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>{tc('delete')}</ListItemText>
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={!!confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => void handleDelete()}
        title={tc('delete')}
        message={t('template.deleteConfirm', { name: confirmDelete?.name ?? '' })}
        confirmColor="error"
      />
    </>
  );
}
